import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Users, DollarSign, Settings, FileText, ArrowLeft,
  Coins, Lock, Unlock, Search, ChevronDown, BarChart3,
  Package, Shield, Plus, Edit, Save, X, Bell, Send, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { Activity, Server, Cpu, HardDrive, Wifi } from 'lucide-react';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const OP_LABELS = {
  brand_monitor: 'Brand Monitoring', sub_monitor: 'Product Monitoring',
  auto_monitor: 'Auto-monitoring', geo_monitor: 'Brand Monitoring (geo)',
  hypothesis_early_check: 'Hypothesis Check', geo_audit: 'GEO Audit',
  generate_strategy: 'Strategy Generation', custom_query: 'Custom Query',
  chat_message: 'Chat Message', chat_parse_url: 'URL Parsing',
  chat_summarize: 'Chat Summary', analyze_sources: 'Source Analysis',
  generate_topics: 'Topic Generation', generate_topics_url: 'Topics from URL',
  generate_article: 'Article', improve_article: 'Article Improvement',
  get_insights: 'Insights', package_purchase: 'Top-up',
  create_project: 'Project Creation', pdf_export: 'PDF Export',
  welcome_bonus: 'Welcome Bonus', admin_manual: 'Admin',
};

const TABS = [
  { id: 'users', label: 'Users', icon: Users },
  { id: 'finance', label: 'Finances', icon: DollarSign },
  { id: 'packages', label: 'Pricing', icon: Package },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'config', label: 'Settings', icon: Settings },
  { id: 'logs', label: 'Logs', icon: FileText },
  { id: 'servers', label: 'Servers', icon: Activity },
];

/* ===== USERS TAB ===== */
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditComment, setCreditComment] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [planForm, setPlanForm] = useState({ plan_id: '', months: 1 });
  const [planLoading, setPlanLoading] = useState(false);
  const [planMessage, setPlanMessage] = useState('');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/users`, {
        params: { search, limit: 50 }, withCredentials: true,
      });
      setUsers(data.users);
      setTotal(data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const loadUserDetail = async (id) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/users/${id}`, { withCredentials: true });
      setSelectedUser(data);
    } catch (e) { toast.error('Loading error'); }
  };

  const handleCredits = async () => {
    if (!selectedUser || !creditAmount || !creditComment.trim()) return;
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/users/${selectedUser.id}/credits`,
        { amount: parseInt(creditAmount), comment: creditComment },
        { withCredentials: true }
      );
      toast.success(`Balance updated: $${(data.new_balance / 100).toFixed(2)}`);
      setCreditAmount(''); setCreditComment('');
      loadUserDetail(selectedUser.id);
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleBlock = async () => {
    if (!selectedUser) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${selectedUser.id}/block`,
        { reason: blockReason || 'No reason' },
        { withCredentials: true }
      );
      toast.success('User blocked');
      setBlockReason('');
      loadUserDetail(selectedUser.id);
      loadUsers();
    } catch (e) { toast.error('Error'); }
  };

  const handleUnblock = async () => {
    if (!selectedUser) return;
    try {
      await axios.post(`${API_URL}/api/admin/users/${selectedUser.id}/unblock`, {}, { withCredentials: true });
      toast.success('User unblocked');
      loadUserDetail(selectedUser.id);
      loadUsers();
    } catch (e) { toast.error('Error'); }
  };
  const handleActivatePlan = async () => {
    if (!selectedUser || !planForm.plan_id || planForm.months < 1) return;
    setPlanLoading(true);
    setPlanMessage('');
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/activate-plan`,
        { user_id: selectedUser.id, plan_id: planForm.plan_id, months: planForm.months },
        { withCredentials: true }
      );
      if (data.success) {
        setPlanMessage(`Plan "${data.plan_name}" for ${data.months} mo. Balance: $${(data.subscription_balance / 100).toFixed(2)}`);
        toast.success(`Plan activated: ${data.plan_name}`);
        loadUserDetail(selectedUser.id);
        setTimeout(() => setPlanMessage(''), 5000);
      } else {
        setPlanMessage(`Error: ${data.error || 'Unknown'}`);
      }
    } catch (e) {
      setPlanMessage(`Error: ${e.response?.data?.detail || 'Network'}`);
      toast.error(e.response?.data?.detail || 'Activation error');
    }
    setPlanLoading(false);
  };

  if (selectedUser) {
    return (
      <div>
        <button onClick={() => setSelectedUser(null)} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6" data-testid="admin-user-back">
          <ArrowLeft className="w-4 h-4" /> Back to list
        </button>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile */}
          <div className="bg-white border border-[#E2DDD8] p-6" data-testid="admin-user-profile">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">Profile</h3>
            <p className="text-lg font-semibold text-slate-900">{selectedUser.name}</p>
            <p className="text-sm text-slate-600 mb-2">{selectedUser.email}</p>
            <p className="text-xs text-slate-500">Role: {selectedUser.role}</p>
            <p className="text-xs text-slate-500">
              Status: <span className={selectedUser.status === 'blocked' ? 'text-red-600' : 'text-emerald-600'}>{selectedUser.status}</span>
            </p>
            <p className="text-xs text-slate-500">
              Registered: {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString('en-US') : '—'}
            </p>
            <div className="mt-4 pt-4 border-t border-[#E2DDD8]">
              {selectedUser.status !== 'blocked' ? (
                <div>
                  <input value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder="Block reason"
                    className="w-full text-sm border border-[#E2DDD8] px-3 py-2 mb-2" />
                  <button onClick={handleBlock} className="w-full flex items-center justify-center gap-2 rounded-none text-xs font-bold uppercase tracking-widest px-3 py-2 bg-red-50 text-red-600 border border-red-200 hover:bg-red-100" data-testid="admin-block-btn">
                    <Lock className="w-3.5 h-3.5" /> Block
                  </button>
                </div>
              ) : (
                <button onClick={handleUnblock} className="w-full flex items-center justify-center gap-2 rounded-none text-xs font-bold uppercase tracking-widest px-3 py-2 bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100" data-testid="admin-unblock-btn">
                  <Unlock className="w-3.5 h-3.5" /> Unblock
                </button>
              )}
            </div>
          </div>

          {/* Wallet */}
          <div className="bg-white border border-[#E2DDD8] p-6" data-testid="admin-user-wallet">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">Wallet</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs text-slate-500">Balance</p>
                <p className="text-2xl font-bold text-slate-900">{'$' + ((selectedUser.wallet?.balance || 0) / 100).toFixed(2)}</p>
                {selectedUser.wallet?.topup_balance > 0 && (
                  <p className="text-xs text-slate-400 mt-1">
                    Top-up: {'$' + ((selectedUser.wallet?.topup_balance || 0) / 100).toFixed(2)}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-500">Purchased</p>
                <p className="text-lg font-semibold text-slate-700">{'$' + ((selectedUser.wallet?.total_purchased || 0) / 100).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Spent</p>
                <p className="text-lg font-semibold text-slate-700">{'$' + ((selectedUser.wallet?.total_spent || 0) / 100).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Bonus</p>
                <p className="text-lg font-semibold text-emerald-600">{'$' + ((selectedUser.wallet?.bonus_credits || 0) / 100).toFixed(2)}</p>
              </div>
            </div>
            {/* No plans on COM — pay-per-use model */}
            <div className="border-t border-[#E2DDD8] pt-4">
              <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Balance Adjustment</h4>
              <input type="number" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} placeholder="Amount (+/-)"
                className="w-full text-sm border border-[#E2DDD8] px-3 py-2 mb-2" data-testid="admin-credit-amount" />
              <input value={creditComment} onChange={(e) => setCreditComment(e.target.value)} placeholder="Comment (required)"
                className="w-full text-sm border border-[#E2DDD8] px-3 py-2 mb-2" data-testid="admin-credit-comment" />
              <button onClick={handleCredits} disabled={!creditAmount || !creditComment.trim()}
                className="w-full rounded-none font-bold tracking-widest uppercase text-xs px-3 py-2 bg-[#1BB594] text-white hover:bg-[#159A7F] disabled:opacity-50" data-testid="admin-credit-submit">
                Apply
              </button>
            </div>
            {/* No plans on COM — pay-per-use model */}
          </div>

          {/* Usage stats */}
          <div className="bg-white border border-[#E2DDD8] p-6" data-testid="admin-user-usage">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">API Usage</h3>
            {Object.keys(selectedUser.usage_stats || {}).length === 0 ? (
              <p className="text-sm text-slate-500">No data</p>
            ) : (
              <div className="space-y-2">
                {Object.entries(selectedUser.usage_stats).map(([op, stats]) => (
                  <div key={op} className="flex justify-between text-sm">
                    <span className="text-slate-600">{OP_LABELS[op] || op}</span>
                    <span className="font-semibold text-slate-900">{stats.count}x (${(stats.total / 100).toFixed(2)})</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent transactions */}
        <div className="bg-white border border-[#E2DDD8] mt-6 p-6" data-testid="admin-user-txs">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">Recent Transactions</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E2DDD8]">
                  <th className="text-left px-4 py-2 text-xs font-bold uppercase text-slate-500">Date</th>
                  <th className="text-left px-4 py-2 text-xs font-bold uppercase text-slate-500">Type</th>
                  <th className="text-left px-4 py-2 text-xs font-bold uppercase text-slate-500">Operation</th>
                  <th className="text-right px-4 py-2 text-xs font-bold uppercase text-slate-500">Amount</th>
                  <th className="text-right px-4 py-2 text-xs font-bold uppercase text-slate-500">Balance</th>
                </tr>
              </thead>
              <tbody>
                {(selectedUser.transactions || []).slice(0, 20).map((tx, i) => (
                  <tr key={i} className="border-b border-[#E2DDD8] last:border-0">
                    <td className="px-4 py-2 text-slate-500">{new Date(tx.created_at).toLocaleString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                    <td className="px-4 py-2">
                      <span className={`text-xs font-bold px-2 py-0.5 ${tx.type === 'debit' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>{tx.type}</span>
                    </td>
                    <td className="px-4 py-2 text-slate-700">{OP_LABELS[tx.operation] || tx.operation || '—'}</td>
                    <td className={`px-4 py-2 text-right font-semibold ${tx.type === 'debit' ? 'text-red-600' : 'text-emerald-600'}`}>
                      {tx.type === 'debit' ? '-' : '+'}${((tx.amount || 0) / 100).toFixed(2)}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-600">{'$' + ((tx.balance_after || 0) / 100).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by email or name..."
            className="w-full pl-10 pr-4 py-2 text-sm border border-[#E2DDD8] bg-white" data-testid="admin-user-search" />
        </div>
        <span className="text-sm text-slate-500">{total} users</span>
      </div>
      {loading ? <p className="text-slate-500">Loading...</p> : (
        <div className="bg-white border border-[#E2DDD8] overflow-hidden" data-testid="admin-users-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E2DDD8]">
                <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Email</th>
                <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Name</th>
                <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Role</th>
                <th className="text-right px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Balance</th>
                <th className="text-right px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Projects</th>
                <th className="px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Project URLs</th>
                <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-[#E2DDD8] last:border-0 hover:bg-slate-50 cursor-pointer"
                  onClick={() => loadUserDetail(u.id)} data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-6 py-3 text-[#1BB594] font-medium">{u.email}</td>
                  <td className="px-6 py-3 text-slate-700">{u.name}</td>
                  <td className="px-6 py-3">
                    <span className={`text-xs font-bold uppercase px-2 py-0.5 ${u.role === 'admin' ? 'bg-[#1BB594]/10 text-[#1BB594]' : 'bg-slate-50 text-slate-600'}`}>{u.role}</span>
                  </td>
                  <td className="px-6 py-3 text-right font-semibold">{'$' + ((u.balance || 0) / 100).toFixed(2)}</td>
                  <td className="px-6 py-3 text-right">{u.monitors_count}</td>
                  <td className="px-6 py-3" style={{maxWidth: '220px', fontSize: '12px'}}>
                    {u.monitor_urls && u.monitor_urls.map((url, i) => (
                      <div key={i}>
                        <a href={url.startsWith('http') ? url : 'https://' + url}
                           target="_blank" rel="noopener noreferrer"
                           className="text-blue-400 hover:text-blue-300 hover:underline"
                           onClick={e => e.stopPropagation()}>
                          {url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                        </a>
                      </div>
                    ))}
                  </td>
                  <td className="px-6 py-3">
                    <span className={`text-xs font-bold uppercase px-2 py-0.5 ${u.status === 'blocked' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>{u.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ===== FINANCE TAB ===== */
const FinanceTab = () => {
  const [dashboard, setDashboard] = useState(null);
  const [pkgStats, setPkgStats] = useState([]);
  const [opsStats, setOpsStats] = useState([]);
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, pkgRes, opsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/billing/dashboard`, { params: { period }, withCredentials: true }),
        axios.get(`${API_URL}/api/admin/billing/packages-stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/billing/operations-stats`, { params: { period }, withCredentials: true }),
      ]);
      setDashboard(dashRes.data);
      setPkgStats(pkgRes.data.stats);
      setOpsStats(opsRes.data.stats);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-slate-500">Loading...</p>;

  const d = dashboard || {};

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        {['7d', '30d', '90d', 'year'].map(p => (
          <button key={p} onClick={() => setPeriod(p)}
            className={`text-xs font-bold uppercase tracking-widest px-3 py-1.5 border ${period === p ? 'bg-[#1BB594] text-white border-[#1BB594]' : 'bg-white border-[#E2DDD8] text-slate-600 hover:bg-slate-50'}`}
            data-testid={`admin-period-${p}`}>
            {p}
          </button>
        ))}
      </div>

      {/* Revenue metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 mb-8" data-testid="admin-revenue-metrics">
        <div className="bg-white border border-[#E2DDD8] p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Revenue</p>
          <p className="text-2xl font-bold text-slate-900">{'$' + ((d.total_revenue || 0) / 100).toFixed(2)}</p>
        </div>
        <div className="bg-white border border-[#E2DDD8] p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Transactions</p>
          <p className="text-2xl font-bold text-slate-900">{d.transactions_count || 0}</p>
        </div>
        <div className="bg-white border border-[#E2DDD8] p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Avg. Check</p>
          <p className="text-2xl font-bold text-slate-900">{'$' + ((d.avg_check || 0) / 100).toFixed(2)}</p>
        </div>
        <div className="bg-white border border-[#E2DDD8] p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Total Users</p>
          <p className="text-2xl font-bold text-slate-900">{d.total_users || 0}</p>
        </div>
      </div>

      {/* Package stats */}
      <h3 className="text-lg font-semibold text-slate-900 mb-4">Top-up History (30d)</h3>
      <div className="bg-white border border-[#E2DDD8] mb-8 overflow-hidden" data-testid="admin-pkg-stats">
        {pkgStats.length === 0 ? (
          <div className="p-6 text-center text-slate-500">No sales for this period</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E2DDD8]">
                <th className="text-left px-6 py-3 text-xs font-bold uppercase text-slate-500">Description</th>
                <th className="text-right px-6 py-3 text-xs font-bold uppercase text-slate-500">Sales</th>
                <th className="text-right px-6 py-3 text-xs font-bold uppercase text-slate-500">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {pkgStats.map((s) => (
                <tr key={s.package_id} className="border-b border-[#E2DDD8] last:border-0">
                  <td className="px-6 py-3 text-slate-700">{s.package_name}</td>
                  <td className="px-6 py-3 text-right font-semibold">{s.sales_count}</td>
                  <td className="px-6 py-3 text-right font-semibold">{'$' + ((s.revenue || 0) / 100).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Operations stats */}
      <h3 className="text-lg font-semibold text-slate-900 mb-4">Usage by Operations</h3>
      <div className="bg-white border border-[#E2DDD8] overflow-hidden" data-testid="admin-ops-stats">
        {opsStats.length === 0 ? (
          <div className="p-6 text-center text-slate-500">No data</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E2DDD8]">
                <th className="text-left px-6 py-3 text-xs font-bold uppercase text-slate-500">Operation</th>
                <th className="text-right px-6 py-3 text-xs font-bold uppercase text-slate-500">Count</th>
                <th className="text-right px-6 py-3 text-xs font-bold uppercase text-slate-500">Amount</th>
              </tr>
            </thead>
            <tbody>
              {opsStats.map((s) => (
                <tr key={s.operation} className="border-b border-[#E2DDD8] last:border-0">
                  <td className="px-6 py-3 text-slate-700">{OP_LABELS[s.operation] || s.operation}</td>
                  <td className="px-6 py-3 text-right">{s.count}</td>
                  <td className="px-6 py-3 text-right font-semibold">{'$' + ((s.total_credits || 0) / 100).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/* ===== PACKAGES TAB ===== */
const PackagesTab = () => {
  const [packages, setPackages] = useState([]);
  const [opCosts, setOpCosts] = useState({});
  const [editingPkg, setEditingPkg] = useState(null);
  const [editingCosts, setEditingCosts] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [pkgRes, costRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/packages`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/operation-costs`, { withCredentials: true }),
      ]);
      setPackages(pkgRes.data);
      setOpCosts(costRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updatePkg = async (pkgId, field, value) => {
    try {
      await axios.put(`${API_URL}/api/admin/packages/${pkgId}`, { [field]: value }, { withCredentials: true });
      toast.success('Package updated');
      load();
    } catch (e) { toast.error('Error'); }
  };

  const saveCosts = async () => {
    try {
      await axios.put(`${API_URL}/api/admin/operation-costs`, { costs: opCosts }, { withCredentials: true });
      toast.success('Costs updated');
      setEditingCosts(false);
    } catch (e) { toast.error('Error'); }
  };

  if (loading) return <p className="text-slate-500">Loading...</p>;

  return (
    <div>
      {/* Operation pricing */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900">Operation Pricing</h3>
        {editingCosts ? (
          <div className="flex gap-2">
            <button onClick={saveCosts} className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-1.5 bg-[#1BB594] text-white" data-testid="admin-save-costs">
              <Save className="w-3.5 h-3.5" /> Save
            </button>
            <button onClick={() => { load(); setEditingCosts(false); }} className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-1.5 bg-white border border-[#E2DDD8] text-slate-600">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        ) : (
          <button onClick={() => setEditingCosts(true)} className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-1.5 bg-white border border-[#E2DDD8] text-slate-600 hover:bg-slate-50" data-testid="admin-edit-costs">
            <Edit className="w-3.5 h-3.5" /> Edit
          </button>
        )}
      </div>
      <div className="bg-white border border-[#E2DDD8] overflow-hidden" data-testid="admin-op-costs-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E2DDD8]">
              <th className="text-left px-6 py-3 text-xs font-bold uppercase text-slate-500">Operation</th>
              <th className="text-right px-6 py-3 text-xs font-bold uppercase text-slate-500">Price</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(opCosts).map(([key, cost]) => (
              <tr key={key} className="border-b border-[#E2DDD8] last:border-0">
                <td className="px-6 py-3 text-slate-700">{OP_LABELS[key] || key}</td>
                <td className="px-6 py-3 text-right">
                  {editingCosts ? (
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-xs text-slate-400">$</span>
                      <input type="number" step="0.01" value={(cost / 100).toFixed(2)}
                        onChange={(e) => setOpCosts(prev => ({ ...prev, [key]: Math.round(parseFloat(e.target.value || 0) * 100) }))}
                        className="w-24 text-right text-sm border border-[#E2DDD8] px-2 py-1" />
                    </div>
                  ) : (
                    <span className="font-semibold">{'$' + (cost / 100).toFixed(2)}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ===== CONFIG TAB ===== */
const ConfigTab = () => {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/config`, { withCredentials: true });
      setConfig(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateConfig = async (key, value) => {
    try {
      await axios.put(`${API_URL}/api/admin/config`, { key, value }, { withCredentials: true });
      toast.success(`${key} updated`);
      load();
    } catch (e) { toast.error('Error'); }
  };

  if (loading) return <p className="text-slate-500">Loading...</p>;

  const CONFIG_LABELS = {
    welcome_credits: 'Welcome Bonus (cents)',
    credits_expiry_days: 'Balance Expiry (days)',
    usd_rub_rate: 'USD/RUB Rate',
    maintenance_mode: 'Maintenance Mode',
    maintenance_message: 'Maintenance Message',
    max_legal_iterations: 'Max Legal Iterations',
  };

  const editableKeys = ['welcome_credits', 'credits_expiry_days', 'usd_rub_rate', 'max_legal_iterations', 'maintenance_mode', 'maintenance_message'];

  return (
    <div>
      <h3 className="text-lg font-semibold text-slate-900 mb-4">System Settings</h3>
      <div className="space-y-4" data-testid="admin-config-list">
        {editableKeys.filter(k => k in config).map(key => (
          <div key={key} className="bg-white border border-[#E2DDD8] p-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-slate-900">{CONFIG_LABELS[key] || key}</p>
              <p className="text-xs text-slate-500">Key: {key}</p>
            </div>
            <div className="flex items-center gap-2">
              {typeof config[key] === 'boolean' ? (
                <button onClick={() => updateConfig(key, !config[key])}
                  className={`text-xs font-bold uppercase px-3 py-1.5 ${config[key] ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-emerald-50 text-emerald-600 border border-emerald-200'}`}
                  data-testid={`admin-config-toggle-${key}`}>
                  {config[key] ? 'Disable' : 'Enable'}
                </button>
              ) : (
                <ConfigInput configKey={key} value={config[key]} onSave={(v) => updateConfig(key, v)} />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Global stats */}
      <h3 className="text-lg font-semibold text-slate-900 mt-8 mb-4">Summary Stats</h3>
      <GlobalStats />
    </div>
  );
};

const ConfigInput = ({ configKey, value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm font-mono bg-slate-50 px-3 py-1">{String(value)}</span>
        <button onClick={() => { setVal(value); setEditing(true); }} className="text-xs text-[#1BB594] hover:underline">Edit</button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input type={typeof value === 'number' ? 'number' : 'text'} value={val}
        onChange={(e) => setVal(typeof value === 'number' ? Number(e.target.value) : e.target.value)}
        className="w-32 text-sm border border-[#E2DDD8] px-2 py-1" />
      <button onClick={() => { onSave(val); setEditing(false); }} className="text-xs font-bold uppercase px-2 py-1 bg-[#1BB594] text-white">OK</button>
      <button onClick={() => setEditing(false)} className="text-xs text-slate-500">Cancel</button>
    </div>
  );
};

const GlobalStats = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/billing/stats`, { withCredentials: true })
      .then(res => setStats(res.data)).catch(() => {});
  }, []);

  if (!stats) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-0" data-testid="admin-global-stats">
      <div className="bg-white border border-[#E2DDD8] p-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Wallets</p>
        <p className="text-2xl font-bold text-slate-900">{stats.total_wallets}</p>
      </div>
      <div className="bg-white border border-[#E2DDD8] p-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Total Balance</p>
        <p className="text-2xl font-bold text-slate-900">{'$' + ((stats.total_balance || 0) / 100).toFixed(2)}</p>
      </div>
      <div className="bg-white border border-[#E2DDD8] p-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Total Purchased</p>
        <p className="text-2xl font-bold text-slate-900">{'$' + ((stats.total_purchased || 0) / 100).toFixed(2)}</p>
      </div>
      <div className="bg-white border border-[#E2DDD8] p-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Total Spent</p>
        <p className="text-2xl font-bold text-slate-900">{'$' + ((stats.total_spent || 0) / 100).toFixed(2)}</p>
      </div>
    </div>
  );
};

/* ===== LOGS TAB ===== */
const LogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/logs`, { params: { limit: 50 }, withCredentials: true })
      .then(res => { setLogs(res.data.logs); setTotal(res.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-slate-500">Loading...</p>;

  return (
    <div>
      <h3 className="text-lg font-semibold text-slate-900 mb-4">Activity Log ({total})</h3>
      <div className="bg-white border border-[#E2DDD8] overflow-hidden" data-testid="admin-logs-table">
        {logs.length === 0 ? (
          <div className="p-6 text-center text-slate-500">No entries</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E2DDD8]">
                <th className="text-left px-6 py-3 text-xs font-bold uppercase text-slate-500">Date</th>
                <th className="text-left px-6 py-3 text-xs font-bold uppercase text-slate-500">Action</th>
                <th className="text-left px-6 py-3 text-xs font-bold uppercase text-slate-500">User</th>
                <th className="text-left px-6 py-3 text-xs font-bold uppercase text-slate-500">Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => (
                <tr key={i} className="border-b border-[#E2DDD8] last:border-0">
                  <td className="px-6 py-3 text-slate-500">{new Date(log.created_at).toLocaleString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                  <td className="px-6 py-3 font-mono text-slate-700">{log.action}</td>
                  <td className="px-6 py-3 text-slate-500 text-xs font-mono">{log.target_user_id || '—'}</td>
                  <td className="px-6 py-3 text-slate-500 text-xs">{JSON.stringify(log.details || {}).slice(0, 100)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/* ===== NOTIFICATIONS TAB ===== */
const NotificationsTab = () => {
  const [broadcasts, setBroadcasts] = useState([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/notifications/broadcast`);
      setBroadcasts(data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/notifications/broadcast`, { title, message });
      toast.success('Notification sent to all users');
      setTitle('');
      setMessage('');
      load();
    } catch (e) {
      toast.error('Send error');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this notification?')) return;
    try {
      await axios.delete(`${API_URL}/api/notifications/broadcast/${id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error('Delete error');
    }
  };

  return (
    <div data-testid="admin-notifications-tab">
      <h2 className="text-lg font-bold text-slate-800 mb-6">User Notifications</h2>

      {/* Send form */}
      <div className="mb-8 p-6 bg-white border border-[#E2DDD8]" data-testid="notification-form">
        <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-4">New Notification</h3>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
          className="w-full px-4 py-2.5 border border-[#E2DDD8] text-sm mb-3 focus:outline-none focus:border-[#1BB594]"
          data-testid="notification-title-input"
        />
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Message text..."
          rows={4}
          className="w-full px-4 py-2.5 border border-[#E2DDD8] text-sm mb-3 focus:outline-none focus:border-[#1BB594] resize-none"
          data-testid="notification-message-input"
        />
        <button
          onClick={handleSend}
          disabled={!title.trim() || !message.trim() || sending}
          className="flex items-center gap-2 px-6 py-2.5 bg-[#1BB594] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#159A7F] disabled:opacity-50"
          data-testid="send-notification-btn"
        >
          <Send className="w-4 h-4" />
          {sending ? 'Sending...' : 'Send to All'}
        </button>
      </div>

      {/* History */}
      <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">Notification History</h3>
      {broadcasts.length === 0 ? (
        <p className="text-sm text-slate-400">No notifications yet</p>
      ) : (
        <div className="space-y-3">
          {broadcasts.map(b => (
            <div key={b.id} className="p-4 bg-white border border-[#E2DDD8] flex justify-between items-start" data-testid={`broadcast-${b.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-800">{b.title}</p>
                <p className="text-xs text-slate-600 mt-1">{b.message}</p>
                <p className="text-[10px] text-slate-400 mt-2">{b.created_at ? new Date(b.created_at).toLocaleString('en-US') : ''}</p>
              </div>
              <button onClick={() => handleDelete(b.id)} className="text-slate-400 hover:text-red-500 ml-3 flex-shrink-0" data-testid={`delete-broadcast-${b.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ===== MAIN ADMIN PANEL ===== */

/* ===== SERVERS TAB ===== */
const MetricCard = ({ title, value, unit, percent, icon: Icon, color }) => (
  <div className="bg-white border border-[#E2DDD8] p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-bold uppercase tracking-widest text-slate-500">{title}</span>
      {Icon && <Icon className={`w-4 h-4 ${color || 'text-slate-400'}`} />}
    </div>
    <div className="flex items-end gap-2">
      <span className="text-2xl font-black text-slate-800">{value}</span>
      <span className="text-xs text-slate-500 mb-1">{unit}</span>
    </div>
    {percent !== undefined && (
      <div className="mt-2 w-full bg-slate-100 h-2">
        <div
          className={`h-full transition-all ${percent > 80 ? 'bg-red-500' : percent > 60 ? 'bg-amber-500' : 'bg-emerald-500'}`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
    )}
  </div>
);

const ServersTab = () => {
  const [currentMetrics, setCurrentMetrics] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedServer, setSelectedServer] = useState('reg_ru');
  const [period, setPeriod] = useState('1h');
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const loadCurrent = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/metrics/current`, { withCredentials: true });
      setCurrentMetrics(data);
    } catch (e) { console.error('Metrics error:', e); }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/metrics/history`, {
        params: { server: selectedServer, period },
        withCredentials: true,
      });
      setHistory((data.data || []).map(d => ({
        ...d,
        time: new Date(d.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      })));
    } catch (e) { console.error('History error:', e); }
  }, [selectedServer, period]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadCurrent(), loadHistory()]).finally(() => setLoading(false));
  }, [loadCurrent, loadHistory]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => { loadCurrent(); }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadCurrent]);

  const reg = currentMetrics?.reg_ru;
  const ai = currentMetrics?.ai_server;
  const queue = currentMetrics?.queue;

  const PERIODS = [
    { id: '1h', label: '1 hour' },
    { id: '6h', label: '6 hours' },
    { id: '1d', label: 'Day' },
    { id: '7d', label: 'Week' },
    { id: '30d', label: 'Month' },
  ];

  if (loading && !currentMetrics) return <div className="text-center py-12 text-slate-500">Loading metrics...</div>;

  return (
    <div className="space-y-8">
      {/* Current Metrics — reg.ru */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Server className="w-4 h-4 text-blue-600" />
          <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700">COM Server (206.189.105.161)</h3>
          {reg && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 font-bold">ONLINE</span>}
          {!reg && <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 font-bold">OFFLINE</span>}
        </div>
        {reg ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard title="CPU" value={reg.cpu_percent} unit="%" percent={reg.cpu_percent} icon={Cpu} color="text-blue-500" />
            <MetricCard title="RAM" value={reg.memory_percent} unit={`% (${reg.memory_used_mb} MB)`} percent={reg.memory_percent} icon={Activity} color="text-purple-500" />
            <MetricCard title="Disk" value={reg.disk_percent} unit={`% (${reg.disk_used_gb} GB)`} percent={reg.disk_percent} icon={HardDrive} color="text-amber-500" />
            <MetricCard title="Load Avg" value={reg.load_avg_1} unit={`/ ${reg.load_avg_5} / ${reg.load_avg_15}`} icon={Activity} color="text-slate-500" />
          </div>
        ) : <p className="text-sm text-red-500">Failed to get metrics</p>}
      </div>

      {/* Current Metrics — AI Server */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Server className="w-4 h-4 text-indigo-600" />
          <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700">AI Server (161.35.156.235)</h3>
          {ai && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 font-bold">ONLINE</span>}
          {!ai && <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 font-bold">OFFLINE</span>}
        </div>
        {ai ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard title="CPU" value={ai.cpu_percent} unit="%" percent={ai.cpu_percent} icon={Cpu} color="text-blue-500" />
            <MetricCard title="RAM" value={ai.memory_percent} unit={`% (${ai.memory_used_mb} MB)`} percent={ai.memory_percent} icon={Activity} color="text-purple-500" />
            <MetricCard title="Disk" value={ai.disk_percent} unit={`% (${ai.disk_used_gb} GB)`} percent={ai.disk_percent} icon={HardDrive} color="text-amber-500" />
            <MetricCard title="Load Avg" value={ai.load_avg_1} unit={`/ ${ai.load_avg_5} / ${ai.load_avg_15}`} icon={Activity} color="text-slate-500" />
          </div>
        ) : <p className="text-sm text-slate-500">AI Server not responding or endpoint not configured</p>}
      </div>

      {/* Queue Status */}
      {queue && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Wifi className="w-4 h-4 text-emerald-600" />
            <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700">Monitoring Queue</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard title="Active" value={queue.active} unit={`of ${queue.max_concurrent}`} percent={(queue.active / queue.max_concurrent) * 100} icon={Activity} color="text-emerald-500" />
            <MetricCard title="Queued" value={queue.waiting} unit="tasks" icon={Activity} color="text-amber-500" />
            <MetricCard title="Available Slots" value={queue.available} unit="" icon={Activity} color="text-blue-500" />
            <div className="bg-white border border-[#E2DDD8] p-4 flex items-center justify-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} className="accent-emerald-500" />
                <span className="text-xs font-bold text-slate-600">Auto-refresh (30 sec)</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* History Charts */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700">Load History</h3>
          <div className="flex gap-2">
            {/* Server selector */}
            <div className="flex gap-0">
              <button onClick={() => setSelectedServer('reg_ru')}
                className={`text-[10px] font-bold px-3 py-1 border ${selectedServer === 'reg_ru' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-[#E2DDD8]'}`}>
                COM
              </button>
              <button onClick={() => setSelectedServer('ai_server')}
                className={`text-[10px] font-bold px-3 py-1 border-t border-b border-r ${selectedServer === 'ai_server' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-[#E2DDD8]'}`}>
                AI Server
              </button>
            </div>
            {/* Period selector */}
            <div className="flex gap-0">
              {PERIODS.map(p => (
                <button key={p.id} onClick={() => setPeriod(p.id)}
                  className={`text-[10px] font-bold px-3 py-1 border ${period === p.id ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-[#E2DDD8]'} ${p.id !== '1h' ? 'border-l-0' : ''}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {history.length > 0 ? (
          <div className="space-y-6">
            {/* CPU Chart */}
            <div className="bg-white border border-[#E2DDD8] p-4">
              <h4 className="text-xs font-bold text-slate-500 mb-3">CPU %</h4>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={history}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2DDD8" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="cpu_percent" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} name="CPU %" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* RAM Chart */}
            <div className="bg-white border border-[#E2DDD8] p-4">
              <h4 className="text-xs font-bold text-slate-500 mb-3">RAM %</h4>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={history}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2DDD8" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="memory_percent" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.1} name="RAM %" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Load Average Chart */}
            <div className="bg-white border border-[#E2DDD8] p-4">
              <h4 className="text-xs font-bold text-slate-500 mb-3">Load Average</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={history}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2DDD8" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="load_avg_1" stroke="#ef4444" strokeWidth={2} dot={false} name="1 min" />
                  <Line type="monotone" dataKey="load_avg_5" stroke="#f59e0b" strokeWidth={2} dot={false} name="5 min" />
                  <Line type="monotone" dataKey="load_avg_15" stroke="#22c55e" strokeWidth={2} dot={false} name="15 min" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-[#E2DDD8] p-8 text-center">
            <p className="text-sm text-slate-500">No historical data. Data will appear after the cron job is configured.</p>
            <p className="text-xs text-slate-400 mt-2">Current metrics are displayed in real time.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const { user, logout } = useAuth();
const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const TabContent = {
    users: UsersTab,
    finance: FinanceTab,
    packages: PackagesTab,
    notifications: NotificationsTab,
    config: ConfigTab,
    logs: LogsTab,
    servers: ServersTab,
  };

  const ActiveComponent = TabContent[activeTab];

  return (
    <div className="min-h-screen bg-[#FFFFFF]" data-testid="admin-panel">
      <header className="bg-white/80 backdrop-blur-xl border-b border-[#E2DDD8] sticky top-0 z-50">
        <div className="w-full max-w-[1400px] mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="text-slate-600 hover:text-slate-900" data-testid="admin-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <img src="/arpai_logo.png" alt="ARPAI" style={{height: '1.25rem'}} />
              <span className="text-xl font-bold text-[#2F2E2F] tracking-[0.15em]">GEO</span>
              <span className="hidden sm:inline text-[10px] text-slate-400 ml-1">AI search monitoring & analytics</span>
              <span className="text-slate-300 mx-1">/</span>
              <h1 className="text-base sm:text-lg tracking-tight font-bold text-slate-500">Admin Panel</h1>
            </div>
          </div>
          <span className="text-sm text-slate-500">{user?.email}</span>
        </div>
        {/* Tabs */}
        <div className="w-full max-w-[1400px] mx-auto px-6 md:px-12">
          <nav className="flex gap-0 -mb-px" data-testid="admin-tabs">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-widest border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#1BB594] text-[#1BB594]'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
                data-testid={`admin-tab-${tab.id}`}>
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="w-full max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        <ActiveComponent />
      </main>
    </div>
  );
};

export default AdminPanel;
